import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  MessageSquare, Plus, Heart, Flag, Trash2, Send, ChevronLeft,
  Sparkles, Clock, User, MessageCircle, Filter
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem
} from "@/components/ui/dropdown-menu";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FORUM_CATEGORIES = [
  { id: "general", label: "General Discussion" },
  { id: "meditation", label: "Meditation" },
  { id: "experiences", label: "Experiences & Insights" },
  { id: "questions", label: "Questions" },
  { id: "techniques", label: "Techniques" },
  { id: "book-club", label: "Book Club" },
];

const CAT_COLORS = {
  general: "bg-[#8FA69A]/15 text-[#2C4A3B]",
  meditation: "bg-[#D4AF37]/15 text-[#8B7520]",
  experiences: "bg-[#C17B5F]/15 text-[#8B4530]",
  questions: "bg-[#6B8EAE]/15 text-[#3A5F7F]",
  techniques: "bg-[#9B8EC4]/15 text-[#5E4F8B]",
  "book-club": "bg-[#E8A87C]/15 text-[#A0623B]",
};

function timeAgo(dateStr) {
  const d = new Date(dateStr);
  const now = new Date();
  const diff = Math.floor((now - d) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString();
}

function PostCard({ post, onLike, onDelete, currentUser }) {
  return (
    <div
      className="p-5 rounded-2xl bg-white border border-[#E3DDD2] hover:shadow-sm transition-all duration-300"
      data-testid={`forum-post-${post.id}`}
    >
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-full bg-[#8FA69A]/10 flex items-center justify-center shrink-0">
          <span className="text-sm font-medium text-[#8FA69A]">{(post.author_name || "A")[0].toUpperCase()}</span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="text-sm font-medium text-[#1A221E]">{post.author_name}</span>
            <span className="text-xs text-[#737E79]">{timeAgo(post.created_at)}</span>
            <Badge className={`${CAT_COLORS[post.category] || CAT_COLORS.general} border-0 rounded-full text-xs`}>
              {post.category}
            </Badge>
          </div>
          <Link to={`/forum/${post.id}`} data-testid={`post-link-${post.id}`}>
            <h3 className="font-heading text-base font-medium text-[#1A221E] hover:text-[#8FA69A] transition-colors cursor-pointer">
              {post.title}
            </h3>
          </Link>
          <p className="text-sm text-[#737E79] mt-1 line-clamp-2 leading-relaxed">{post.content}</p>
          <div className="flex items-center gap-4 mt-3">
            <button
              onClick={() => onLike(post.id)}
              data-testid={`like-post-${post.id}`}
              className="flex items-center gap-1 text-xs text-[#737E79] hover:text-[#D4AF37] transition-colors"
            >
              <Heart className={`w-3.5 h-3.5 ${post.liked_by?.includes(currentUser?._id || currentUser?.id) ? "fill-[#D4AF37] text-[#D4AF37]" : ""}`} strokeWidth={1.5} />
              {post.likes || 0}
            </button>
            <Link
              to={`/forum/${post.id}`}
              className="flex items-center gap-1 text-xs text-[#737E79] hover:text-[#8FA69A] transition-colors"
            >
              <MessageCircle className="w-3.5 h-3.5" strokeWidth={1.5} />
              {post.comment_count || 0}
            </Link>
            {currentUser && (post.author_id === (currentUser._id || currentUser.id) || currentUser.role === "admin") && (
              <button
                onClick={() => onDelete(post.id)}
                data-testid={`delete-post-${post.id}`}
                className="flex items-center gap-1 text-xs text-[#737E79] hover:text-red-500 transition-colors ml-auto"
              >
                <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ForumPage() {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [catFilter, setCatFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newContent, setNewContent] = useState("");
  const [newCategory, setNewCategory] = useState("general");
  const [submitting, setSubmitting] = useState(false);

  const fetchPosts = async (p, cat) => {
    try {
      const params = { page: p, limit: 20 };
      if (cat) params.category = cat;
      const { data } = await axios.get(`${API}/forum/posts`, { params });
      setPosts(data.posts);
      setTotal(data.total);
      setTotalPages(data.pages);
      setLoading(false);
    } catch {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPosts(page, catFilter);
  }, [page, catFilter]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newTitle.trim() || !newContent.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/forum/posts`, { title: newTitle, content: newContent, category: newCategory });
      toast.success("Post shared with the community");
      setShowCreate(false);
      setNewTitle("");
      setNewContent("");
      fetchPosts(1, catFilter);
      setPage(1);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create post");
    } finally {
      setSubmitting(false);
    }
  };

  const handleLike = async (postId) => {
    if (!user) { toast.error("Sign in to like posts"); return; }
    try {
      await axios.post(`${API}/forum/posts/${postId}/like`);
      fetchPosts(page, catFilter);
    } catch { toast.error("Failed"); }
  };

  const handleDelete = async (postId) => {
    try {
      await axios.delete(`${API}/forum/posts/${postId}`);
      toast("Post removed");
      fetchPosts(page, catFilter);
    } catch { toast.error("Failed to delete"); }
  };

  return (
    <main className="min-h-screen" data-testid="forum-page">
      <div className="max-w-4xl mx-auto px-6 md:px-12 pt-8 pb-16">
        {/* Header */}
        <div className="flex items-start justify-between mb-8">
          <div>
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#D4AF37] mb-2">Sangha</p>
            <h1 className="font-heading text-4xl sm:text-5xl tracking-tighter font-light text-[#1A221E]">
              Community
            </h1>
            <p className="mt-2 text-sm text-[#737E79]">
              {total} post{total !== 1 ? "s" : ""} from seekers on the path
            </p>
          </div>
          {user && (
            <button
              onClick={() => setShowCreate(true)}
              data-testid="create-post-btn"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#8FA69A] text-white rounded-full text-sm font-medium hover:bg-[#2C4A3B] transition-all duration-300"
            >
              <Plus className="w-4 h-4" strokeWidth={1.5} />
              Share
            </button>
          )}
        </div>

        {/* Category filter */}
        <div className="flex flex-wrap gap-2 mb-6">
          <button
            data-testid="forum-filter-all"
            onClick={() => { setCatFilter(""); setPage(1); }}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-300 ${
              !catFilter ? "bg-[#8FA69A] text-white" : "bg-white border border-[#E3DDD2] text-[#737E79] hover:bg-[#EAE2D6]"
            }`}
          >
            All
          </button>
          {FORUM_CATEGORIES.map((cat) => (
            <button
              key={cat.id}
              data-testid={`forum-filter-${cat.id}`}
              onClick={() => { setCatFilter(cat.id); setPage(1); }}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-300 ${
                catFilter === cat.id ? "bg-[#8FA69A] text-white" : "bg-white border border-[#E3DDD2] text-[#737E79] hover:bg-[#EAE2D6]"
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Posts list */}
        {loading ? (
          <div className="flex items-center justify-center py-20" data-testid="forum-loading">
            <div className="w-8 h-8 rounded-full border-2 border-[#8FA69A] border-t-transparent animate-spin" />
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-20" data-testid="forum-empty">
            <MessageSquare className="w-10 h-10 text-[#E3DDD2] mx-auto mb-3" strokeWidth={1.5} />
            <p className="text-[#737E79]">No posts yet. Be the first to share your insights!</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="forum-posts-list">
            {posts.map((post) => (
              <PostCard key={post.id} post={post} onLike={handleLike} onDelete={handleDelete} currentUser={user} />
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center gap-2 mt-8" data-testid="forum-pagination">
            {Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i + 1}
                onClick={() => setPage(i + 1)}
                className={`w-8 h-8 rounded-full text-sm ${
                  page === i + 1 ? "bg-[#8FA69A] text-white" : "bg-white border border-[#E3DDD2] text-[#737E79] hover:bg-[#EAE2D6]"
                } transition-all`}
              >
                {i + 1}
              </button>
            ))}
          </div>
        )}

        {/* Not signed in message */}
        {!user && (
          <div className="mt-8 p-6 rounded-2xl bg-white border border-[#E3DDD2] text-center" data-testid="forum-login-prompt">
            <p className="text-sm text-[#737E79] mb-3">Sign in to join the conversation and share your insights.</p>
            <Link to="/login" className="inline-flex items-center gap-2 px-4 py-2 bg-[#8FA69A] text-white rounded-full text-sm font-medium hover:bg-[#2C4A3B] transition-all">
              Sign In
            </Link>
          </div>
        )}
      </div>

      {/* Create Post Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="rounded-2xl bg-white border-[#E3DDD2] max-w-lg" data-testid="create-post-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[#1A221E]">Share with the Community</DialogTitle>
            <DialogDescription className="text-sm text-[#737E79]">
              All posts are reviewed by our AI moderator to ensure a respectful, safe space.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4 mt-2">
            <div>
              <Label className="text-sm text-[#1A221E]">Category</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {FORUM_CATEGORIES.map((cat) => (
                  <button
                    key={cat.id}
                    type="button"
                    data-testid={`select-category-${cat.id}`}
                    onClick={() => setNewCategory(cat.id)}
                    className={`px-3 py-1 rounded-full text-xs transition-all ${
                      newCategory === cat.id ? "bg-[#8FA69A] text-white" : "bg-[#F0EDE6] text-[#737E79] hover:bg-[#EAE2D6]"
                    }`}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label htmlFor="post-title" className="text-sm text-[#1A221E]">Title</Label>
              <Input
                id="post-title"
                data-testid="post-title-input"
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                placeholder="What's on your mind?"
                required
                className="mt-1 rounded-xl bg-white border-[#E3DDD2]"
              />
            </div>
            <div>
              <Label htmlFor="post-content" className="text-sm text-[#1A221E]">Content</Label>
              <textarea
                id="post-content"
                data-testid="post-content-input"
                value={newContent}
                onChange={(e) => setNewContent(e.target.value)}
                placeholder="Share your experience, insight, or question..."
                required
                rows={5}
                className="mt-1 w-full rounded-xl bg-white border border-[#E3DDD2] p-3 text-sm resize-none focus:ring-[#8FA69A] focus:border-[#8FA69A] focus:outline-none"
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              data-testid="submit-post-btn"
              className="w-full py-3 bg-[#8FA69A] text-white rounded-full text-sm font-medium hover:bg-[#2C4A3B] transition-all disabled:opacity-50"
            >
              {submitting ? "Reviewing & Posting..." : "Post"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </main>
  );
}
